import type { FC } from 'react';
import { Button, FormHelperText } from '@selfinspectionorg/ui-kit';
import OTPInput from 'react18-input-otp';
import { QRCodeSVG } from 'qrcode.react';
import styles from './Mfa.module.scss';
import { CopyButton } from './CopyButton';

export const TOTP_CODE_LENGTH = 6;

interface Props {
  /** The provisioning URI for the QR code; absent until the setup material loads. */
  otpauthUri?: string;
  /** The shared secret for manual entry; absent until the setup material loads. */
  secret?: string;
  code: string;
  onCodeChange: (code: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  error?: string | null;
}

/**
 * Presentational core of the authenticator-app setup step: QR code, manual-entry key, and the code
 * field. Owns no data-fetching — both the login-time enrollment ceremony and the session-authorized
 * Security-page setup drive it, supplying the setup material and the submit behavior.
 */
export const TotpSetupView: FC<Props> = ({ otpauthUri, secret, code, onCodeChange, onSubmit, isSubmitting, error }) => (
  <div className={styles.body}>
    <div className={styles.section}>
      {otpauthUri && (
        <div className={styles.qrWrapper}>
          <QRCodeSVG value={otpauthUri} size={180} data-testid="mfa-totp-qr" />
        </div>
      )}
      {secret && (
        <div className={styles.secret}>
          <span>Can't scan it? Enter this key manually:</span>
          <span className={styles.secretRow}>
            <code className={styles.secretValue} data-testid="mfa-totp-secret">
              {secret}
            </code>
            <CopyButton value={secret} testId="mfa-totp-secret-copy" />
          </span>
        </div>
      )}
    </div>

    <hr className={styles.divider} />

    <div className={styles.section}>
      <span className={styles.sectionTitle}>Enter the code from your authenticator app</span>
      <div className={styles.codeInputWrapper}>
        <OTPInput
          value={code}
          onChange={onCodeChange}
          numInputs={TOTP_CODE_LENGTH}
          containerStyle={styles.codeInputWrapper}
          inputStyle={styles.codeInput}
          isInputNum
          shouldAutoFocus
          isDisabled={isSubmitting}
          data-testid="mfa-totp-code-input"
        />
      </div>
      <Button
        width="100%"
        onClick={onSubmit}
        disabled={code.length !== TOTP_CODE_LENGTH || isSubmitting}
        isLoading={isSubmitting}
        data-testid="mfa-totp-confirm-button"
      >
        Confirm
      </Button>
      {error && <FormHelperText errored>{error}</FormHelperText>}
    </div>
  </div>
);
